import moment from 'moment-timezone';

/**
 * Convert UTC timestamp to local timezone (Asia/Singapore)
 * @param {string} utcTimestamp - UTC timestamp string
 * @returns {string} Local timestamp in desired format
 */
export const toLocalTime = (utcTimestamp) => {
  if (!utcTimestamp) return '-';
  return moment.utc(utcTimestamp).tz('Asia/Singapore').format('YYYY-MM-DD HH:mm:ss');
};

/**
 * Convert UTC timestamp to local timezone with timezone info
 * @param {string} utcTimestamp - UTC timestamp string
 * @returns {string} Local timestamp with timezone abbreviation
 */
export const toLocalTimeWithTZ = (utcTimestamp) => {
  if (!utcTimestamp) return '-';
  return moment.utc(utcTimestamp).tz('Asia/Singapore').format('YYYY-MM-DD HH:mm:ss z');
};

/**
 * Get current local time
 * @returns {string} Current local time
 */
export const getCurrentLocalTime = () => {
  return moment().tz('Asia/Singapore').format('YYYY-MM-DD HH:mm:ss');
};

/**
 * Format duration in human readable format
 * @param {number} milliseconds - Duration in milliseconds
 * @returns {string} Human readable duration
 */
export const formatDuration = (milliseconds) => {
  if (!milliseconds || milliseconds < 1000) {
    return `${milliseconds}ms`;
  }

  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
};

/**
 * Check if timestamp is within last X minutes
 * @param {string} timestamp - UTC timestamp
 * @param {number} minutes - Number of minutes to check
 * @returns {boolean} True if timestamp is within last X minutes
 */
export const isWithinLastMinutes = (timestamp, minutes) => {
  if (!timestamp) return false;
  const now = moment();
  const ts = moment.utc(timestamp);
  return now.diff(ts, 'minutes') <= minutes;
};

/**
 * Get relative time (e.g., "2 minutes ago")
 * @param {string} utcTimestamp - UTC timestamp
 * @returns {string} Relative time string
 */
export const getRelativeTime = (utcTimestamp) => {
  if (!utcTimestamp) return '-';
  return moment.utc(utcTimestamp).tz('Asia/Singapore').fromNow();
};